class SocialCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.dotsContainer = this.querySelector('[data-carousel-dots]');

    if (!this.track) return;

    this.realSlides = Array.from(this.track.children);
    this.slideCount = this.realSlides.length;

    if (this.slideCount === 0) return;

    this.cloneCount = Math.min(
      parseInt(this.dataset.slidesDesktop || '3', 10),
      this.slideCount
    );

    this.buildLoop();
    this.buildDots();
    this.bindEvents();

    requestAnimationFrame(() => {
      this.resetToRealStart();
      this.updateActiveDot();
    });
  }

  buildLoop() {
    if (this.slideCount <= 1) return;

    const headClones = this.realSlides
      .slice(0, this.cloneCount)
      .map((slide) => this.cloneSlide(slide));
    const tailClones = this.realSlides
      .slice(this.slideCount - this.cloneCount)
      .map((slide) => this.cloneSlide(slide));

    tailClones.forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    headClones.forEach((clone) => this.track.appendChild(clone));

    this.firstRealSlide = this.realSlides[0];
    this.lastRealSlide = this.realSlides[this.slideCount - 1];
  }

  cloneSlide(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-clone', 'true');
    clone.removeAttribute('data-slide-index');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
    return clone;
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dots = this.realSlides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'social-carousel__dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.addEventListener('click', () => this.goToRealIndex(index));
      this.dotsContainer.appendChild(dot);
      return dot;
    });
  }

  bindEvents() {
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

    let scrollEndTimer;
    this.track.addEventListener('scroll', () => {
      this.updateActiveDot();
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => this.correctLoopPosition(), 120);
    });

    window.addEventListener('resize', () => this.resetToRealStart());

    this.bindDrag();
  }

  bindDrag() {
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const onDown = (event) => {
      moved = false;

      if (event.target.closest('.social-carousel__hotspot')) {
        isDown = false;
        return;
      }

      isDown = true;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture && event.pointerId != null && this.track.setPointerCapture(event.pointerId);
    };

    const onMove = (event) => {
      if (!isDown) return;
      const deltaX = event.clientX - startX;
      if (Math.abs(deltaX) > 3) moved = true;
      this.track.scrollLeft = startScroll - deltaX;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-dragging');
      this.snapToNearest();
    };

    this.track.addEventListener('pointerdown', onDown);
    this.track.addEventListener('pointermove', onMove);
    this.track.addEventListener('pointerup', onUp);
    this.track.addEventListener('pointercancel', onUp);
    this.track.addEventListener('pointerleave', () => {
      if (isDown) onUp();
    });

    this.track.addEventListener(
      'click',
      (event) => {
        if (moved) event.preventDefault();
      },
      true
    );
  }

  getSlideStep() {
    const slide = this.track.children[0];
    if (!slide) return 0;
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap || '0');
    return slide.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.track.scrollBy({ left: direction * this.getSlideStep(), behavior: 'smooth' });
  }

  snapToNearest() {
    const step = this.getSlideStep();
    if (!step) return;
    const nearest = Math.round(this.track.scrollLeft / step) * step;
    this.track.scrollTo({ left: nearest, behavior: 'smooth' });
  }

  resetToRealStart() {
    if (!this.firstRealSlide) return;
    this.realTrackStart = this.firstRealSlide.offsetLeft;
    this.realTrackEnd = this.lastRealSlide.offsetLeft + this.lastRealSlide.offsetWidth;
    this.realTrackWidth = this.realTrackEnd - this.realTrackStart;
    if (Math.abs(this.track.scrollLeft) < 1) {
      this.track.scrollLeft = this.realTrackStart;
    }
  }

  correctLoopPosition() {
    if (!this.realTrackWidth) return;
    if (this.track.scrollLeft < this.realTrackStart - 1) {
      this.track.scrollLeft += this.realTrackWidth;
    } else if (this.track.scrollLeft > this.realTrackEnd) {
      this.track.scrollLeft -= this.realTrackWidth;
    }
    this.updateActiveDot();
  }

  goToRealIndex(index) {
    const target = this.realSlides[index];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  updateActiveDot() {
    if (!this.dots || !this.realTrackWidth) return;
    let relative = (this.track.scrollLeft - this.realTrackStart) % this.realTrackWidth;
    if (relative < 0) relative += this.realTrackWidth;
    const step = this.getSlideStep();
    const index = Math.round(relative / step) % this.slideCount;
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }
}

if (!customElements.get('social-carousel')) {
  customElements.define('social-carousel', SocialCarousel);
}
